import React, { CSSProperties, useEffect, useState } from 'react';
import { Button, Col, Image, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { CampaignCreateModal } from '../../../app/brand/campaign/briefing/modal/CampaignCreateModal';
import { ExitImpersonation } from '../../../app/shared/impersonate/components/ExitImpersonation';
import { AuthService } from '../../../authentication/service';
import { UserRoles } from '../../../enum/UserRoles';
import { IUserNotification } from '../../../model/campaign/Campaign';
import { IAllStates } from '../../../store/rootReducer';
import { ErrorToast } from '../../../utils/toasters';
import { CustomImageHandler } from '../../components/CustomImageHandler';
import NotificationBell from '../../components/NotificationBell';
import { getPackageColorHex } from '../../functions/Functions';
import usePagination from '../../hooks/usePagination';
import ProfileMenu from '../menu/ProfileMenu';
import { SideOverlay } from '../SideOverlay';
import { navigateByRole } from '../../../utils/helpers/navigateByRole';
import StripeHeaderComponent from '../../components/stripe/StripeHeaderComponent';
import { BrandModuleSwitcher } from './BrandModuleSwitcher';
import { actions } from '../../../store/authentication/authRedux';
import { useMediaQuery } from '@mui/material';
import { BootstrapBreakpoints } from '../../../enum/BootstrapBreakpoints';

const Header = ({ isProfileSideBarOpen, setProfileSideBar }: any) => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const { redDots } = useSelector((state: IAllStates) => state.redDots);
    const user = useSelector((states: IAllStates) => states.auth.user);
    const isGlobalBrandActive = useSelector((states: IAllStates) => states?.auth?.isGlobalBrand);
    const isClient = user?.isGeneralClient;
    const userType = user?.userType;
    const isGlobalDiscovery = user?.company?.package?.globalDiscovery;
    const isMobile = useMediaQuery(`(max-width: ${BootstrapBreakpoints.MD}px)`);

    const [showBriefingModal, setShowBriefingModal] = useState(false);
    const [isNotificationsOpened, setIsNotificationsOpened] = useState(false);
    const [notificationList, setNotificationList] = useState<IUserNotification[]>([]);
    const { filterParams, setFilterParams } = usePagination({
        perPage: 10,
        otherKeys: {
            isDetailed: true,
        },
    });
    const isBriefingOpen = location?.pathname?.includes('overview') || location?.pathname?.includes('step');
    const isBrandUserType = [UserRoles.BRAND_MASTER, UserRoles.BRAND].includes(userType as UserRoles);
    const isGlobalBrandVisible = process.env.VITE_ENABLE_GLOBAL === 'true' && isBrandUserType;
    const packageBorder: CSSProperties =
        userType === UserRoles.INFLUENCER
            ? {}
            : {
                  border: '2px solid',
                  borderColor: user?.company?.package?.color || getPackageColorHex(user?.company?.package?.name),
              };

    useEffect(() => {
        if (isNotificationsOpened) {
            AuthService.getNotifications(filterParams)
                .then(response => {
                    setNotificationList(response?.data);
                })
                .catch(err => ErrorToast(err));
        }
    }, [isNotificationsOpened, filterParams]);

    useEffect(() => {
        if (redDots?.notifications) {
            setFilterParams((prev: any) => ({ ...prev, updatedAt: new Date()?.getTime() }));
        }
    }, [redDots?.notifications]);

    const openProfile = () => {
        setProfileSideBar(!isProfileSideBarOpen);
    };

    const LOGO_PATHS: Record<'global' | 'campaign' | 'default', { desktop: string; mobile: string }> = {
        global: {
            desktop: '/icons/logos/img-global-logo.svg',
            mobile: '/icons/logos/img-global-logo-mobile.svg',
        },
        campaign: {
            desktop: '/icons/logos/img-campaign-logo.svg',
            mobile: '/icons/logos/img-campaign-logo-mobile.svg',
        },
        default: {
            desktop: '/icons/logos/influence-vision-logo.svg',
            mobile: '/icons/logos/logoSmall.svg',
        },
    };

    const selectValidLogo = (): string => {
        const logoType = !isBrandUserType ? 'default' : isGlobalBrandActive ? 'global' : 'campaign';
        return LOGO_PATHS[logoType][isMobile ? 'mobile' : 'desktop'];
    };
    const openModal = () => setShowBriefingModal(true);

    return (
        <>
            <CampaignCreateModal show={showBriefingModal} closeModal={() => setShowBriefingModal(false)} />
            <header className="py-1">
                <Row className="w-100 m-0 align-items-center">
                    <Col className="col-auto d-flex align-items-center">
                        {isGlobalBrandVisible && (
                            <BrandModuleSwitcher
                                activeModule={isGlobalBrandActive ? 'global' : 'campaign'}
                                onSwitchModule={() => {
                                    dispatch(actions.setIsGlobalBrand(!isGlobalBrandActive));
                                }}
                            />
                        )}
                        <Link to={navigateByRole(userType, isGlobalBrandActive)}>
                            <Image alt="Influence vision logo" src={selectValidLogo()} width={150} />
                        </Link>
                        {/* <div className="d-flex mr-4">
                            <BreadCrumbs/>
                        </div> */}
                        <ExitImpersonation />
                    </Col>
                    <Col className="d-flex justify-content-center">
                        {isGlobalDiscovery ? <StripeHeaderComponent /> : null}
                    </Col>
                    <Col className="col-auto d-flex align-items-center justify-content-end">
                        {!(
                            isClient ||
                            isBriefingOpen ||
                            ![UserRoles.BRAND_MASTER, UserRoles.BRAND].includes(userType as UserRoles) ||
                            isGlobalDiscovery ||
                            isGlobalBrandActive
                        ) && (
                            <Button variant="outline-primary" className={`mr-4`} onClick={openModal} size="sm">
                                {t('brand.create_new_campaign')}
                            </Button>
                        )}
                        {/*<div className="position-relative mr-4" ref={wrapperRef}>*/}

                        {/*    {isNotificationsOpened &&*/}

                        {/*</div>*/}
                        {!isGlobalDiscovery && !isGlobalBrandActive && (
                            <NotificationBell
                                setIsNotificationsOpened={setIsNotificationsOpened}
                                isNotificationsOpened={isNotificationsOpened}
                                setFilterParams={setFilterParams}
                                notificationList={notificationList}
                            />
                        )}
                        <div className="d-flex flex-column align-items-end mr-3">
                            <div className="mb-0">
                                {[UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles)
                                    ? user?.agencyCompany?.displayName
                                    : user.company?.displayName}
                            </div>
                            {(userType === UserRoles.BRAND_MASTER ||
                                userType === UserRoles.BRAND ||
                                userType === UserRoles.AGENCY ||
                                userType === UserRoles.AGENCY_MASTER) && (
                                <div className="d-flex">
                                    {[UserRoles.BRAND_MASTER, UserRoles.AGENCY_MASTER].includes(userType) && (
                                        <span className="status pending mr-2 font-weight-normal">ADMIN</span>
                                    )}
                                    <h5 className="mb-0">
                                        {user.firstName} {user.lastName}
                                    </h5>
                                </div>
                            )}
                            {/*<button*/}
                            {/*    className={`btn-sm btn-outline-secondary text-primary p-1 ml-3 ${(userType === UserRoles.BRAND
                            || userType === UserRoles.BRAND_MASTER) ? '' : 'd-none'}`}>*/}
                            {/*    <Image*/}
                            {/*        className="mr-1"*/}
                            {/*        src="/icons/img-starter-plan.png"*/}
                            {/*        alt="Darts icon"*/}
                            {/*    />*/}
                            {/*    {t('brand.starter_plan')}*/}
                            {/*</button>*/}
                            {userType === UserRoles.INFLUENCER && (
                                <>
                                    <h5 className="mb-0">{user.displayName}</h5>
                                    <div className="mb-0">
                                        {user.firstName} {user.lastName}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="profile-img">
                            <CustomImageHandler
                                altTag="User Profile"
                                thumbnailPath={user?.profilePhoto?.thumbnail}
                                photoPath={user?.profilePhoto?.path}
                                style={packageBorder}
                                onClick={openProfile}
                                classes="new-avatar"
                                name={user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                                isSquare={true}
                            />
                        </div>
                    </Col>
                </Row>
            </header>
            <header className="mobile">
                <div className="d-flex align-items-center">
                    {isGlobalBrandVisible && (
                        <BrandModuleSwitcher
                            activeModule={isGlobalBrandActive ? 'global' : 'campaign'}
                            onSwitchModule={() => {
                                dispatch(actions.setIsGlobalBrand(!isGlobalBrandActive));
                            }}
                        />
                    )}
                    <Link to={navigateByRole(userType, isGlobalBrandActive)}>
                        <img height={34} src={selectValidLogo()} alt="Influence vision logo" />
                    </Link>
                </div>
                <Button
                    variant="outline-primary"
                    className={`${
                        isClient ||
                        isBriefingOpen ||
                        isGlobalDiscovery ||
                        isGlobalBrandActive ||
                        ![UserRoles.BRAND_MASTER, UserRoles.BRAND].includes(userType as UserRoles)
                            ? 'd-none'
                            : null
                    }`}
                    onClick={openModal}
                    size="sm"
                >
                    {t('brand.create_new_campaign')}
                </Button>
                <ExitImpersonation />
                {isGlobalDiscovery ? <StripeHeaderComponent /> : null}
                <div className="profile-img d-flex align-items-center">
                    {!isGlobalDiscovery && !isGlobalBrandActive && (
                        <NotificationBell
                            setIsNotificationsOpened={setIsNotificationsOpened}
                            isNotificationsOpened={isNotificationsOpened}
                            isMobile
                            setFilterParams={setFilterParams}
                            notificationList={notificationList}
                        />
                    )}
                    <CustomImageHandler
                        altTag="User Profile"
                        onClick={openProfile}
                        style={packageBorder}
                        photoPath={user.profilePhoto?.path as string}
                        classes="new-avatar"
                        name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                        isSquare={true}
                    />
                </div>
            </header>
            <SideOverlay
                isOpen={isProfileSideBarOpen}
                side={'right'}
                component={<ProfileMenu toggle={openProfile} />}
            />
        </>
    );
};

export default Header;
